import { Injectable, APP_INITIALIZER, Inject, DOCUMENT } from '@angular/core';

import {
  BootstrapperBase,
  LogService,
  TOKENS,
} from '@trustedshops/tswp-core-common';
import {
  TOKENS as TOKENS_ANALYTICS,
  TrackerType,
  TrackingService,
} from '@trustedshops/tswp-core-analytics';
import {
  TOKENS as MASTERDATA_TOKENS,
  ProductFeatureService,
} from '@trustedshops/tswp-core-masterdata';
import { Environment, ENVIRONMENT } from '../models/environment.type';
import { UserCentricsConsent } from '../core/user-centrics-consent';

/**
 * @deprecated CC-1147: after the migration to the basic/plus model there is no more account type.
 */
type AccountSubscriptionStatus = 'free' | 'paid' | 'unknown';

@Injectable()
export class GoogleAnalyticsTrackingBootstrapper
  extends UserCentricsConsent
  implements BootstrapperBase
{
  public static readonly TYPE = 'GoogleAnalyticsTrackingBootstrapper';

  private static readonly FEAT_ONBOARDING_FREE = 'FEAT_ONBOARDING_FREE';
  private static readonly FEAT_ONBOARDING_PAID = 'FEAT_ONBOARDING_PAID';

  public constructor(
    @Inject(TOKENS_ANALYTICS.TrackingService)
    private readonly _trackingService: TrackingService,
    @Inject(ENVIRONMENT) private readonly _environment: Environment,
    @Inject(DOCUMENT) readonly _document: Document,
    @Inject(MASTERDATA_TOKENS.ProductFeatureService)
    private readonly _productFeatureService: ProductFeatureService,
    @Inject(TOKENS.LogService)
    private readonly logService: LogService
  ) {
    super(_document);
  }

  public async initialize(): Promise<void> {
    const hasGoogleAnalytics4Consent = await this.hasGoogleAnalytics4Consent();
    this.ensureGoogleAnalyticsTrackerRegistered(hasGoogleAnalytics4Consent);
  }

  private async hasGoogleAnalytics4Consent(): Promise<boolean> {
    return await this.userConsentObtained('Google Analytics 4');
  }

  private async ensureGoogleAnalyticsTrackerRegistered(
    hasGoogleAnalytics4Consent: boolean
  ): Promise<void> {
    const googleAnalytics4Property =
      this._environment.tracking.googleAnalytics4;

    if (
      !hasGoogleAnalytics4Consent ||
      this._trackingService.trackers.includes(googleAnalytics4Property)
    ) {
      return;
    }

    try {
      const subscriptionStatus: AccountSubscriptionStatus =
        await this.getAccountSubscription();

      this._trackingService.registerTracker(TrackerType.GoogleAnalytics, {
        property: googleAnalytics4Property,
        anonymize_ip: true,
        send_page_view: true,
        account_type: subscriptionStatus,
        // The tracker factory appends ?id=<property> to this URI, so the
        // loader downloads the full container in one request.
        gtagScriptUri: 'https://www.googletagmanager.com/gtag/js',
      });
    } catch (error: unknown) {
      this.logService.error(
        GoogleAnalyticsTrackingBootstrapper.TYPE,
        new Error('Google Analytics init has failed'),
        error
      );
    }
  }

  /**
   * @deprecated CC-1147: after the migration to the basic/plus model there is no more account type
   * (free/paid), so the account subscription status no longer needs to be determined or tracked.
   * This will be removed once a basic/plus tracking dimension is defined.
   */
  private async getAccountSubscription(): Promise<AccountSubscriptionStatus> {
    try {
      // CC-1236 / PSUP-5378: paid accounts also carry FEAT_ONBOARDING_FREE (via PROD_BASIC_ACCESS),
      // so FEAT_ONBOARDING_PAID must be checked FIRST — checking free first mis-tracked every paid
      // account as 'free'. Mirrors the paid-priority precedence used in the onboarding service / header bar.
      const hasPaidOnboarding = await this._productFeatureService.hasFeature({
        featureNames: GoogleAnalyticsTrackingBootstrapper.FEAT_ONBOARDING_PAID,
      });
      if (hasPaidOnboarding) {
        return 'paid';
      }

      const hasFreeOnboarding = await this._productFeatureService.hasFeature({
        featureNames: GoogleAnalyticsTrackingBootstrapper.FEAT_ONBOARDING_FREE,
      });
      if (hasFreeOnboarding) {
        return 'free';
      }

      return 'unknown';
    } catch (error: unknown) {
      this.logService.error(
        GoogleAnalyticsTrackingBootstrapper.TYPE,
        new Error('Get Account Subscription has failed'),
        error
      );

      return 'unknown';
    }
  }
}

export const GoogleAnalyticsTrackingInitializer = [
  GoogleAnalyticsTrackingBootstrapper,
  {
    provide: APP_INITIALIZER,
    multi: true,
    useFactory: (initializer: GoogleAnalyticsTrackingBootstrapper) => {
      return () => initializer.initialize();
    },
    deps: [GoogleAnalyticsTrackingBootstrapper],
  },
];
